import { useState } from 'react';
import { AlertCircle, CheckCircle2, Phone, MessageSquare, Video, Bell, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function TwilioIntegration() {
  const [isConnected, setIsConnected] = useState(false);
  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');

  const handleConnect = (event: React.FormEvent) => {
    event.preventDefault();
    if (accountSid && authToken) {
      setIsConnected(true);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="flex-1 p-8">
        <header className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Twilio Integration</h1>
          <p className="text-gray-600">
            Connect your Twilio account to enable powerful communication features in your application.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Connect to Twilio</CardTitle>
              <CardDescription>Enter your Twilio API credentials to establish the connection.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConnect} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="account-sid">Account SID</Label>
                  <Input
                    id="account-sid"
                    type="text"
                    placeholder="Enter your Twilio Account SID"
                    value={accountSid}
                    onChange={(e) => setAccountSid(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-token">Auth Token</Label>
                  <Input
                    id="auth-token"
                    type="password"
                    placeholder="Enter your Twilio Auth Token"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="environment">Environment</Label>
                  <Select defaultValue="production">
                    <SelectTrigger id="environment">
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="test">Test</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  {isConnected ? 'Connected' : 'Connect'} <Phone className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </CardContent>
            <CardFooter className="justify-between">
              <p className="text-sm text-gray-500">Need help finding your credentials?</p>
              <Button variant="outline">View Twilio Console</Button>
            </CardFooter>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Connection Status</CardTitle>
              </CardHeader>
              <CardContent>
                {isConnected ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Not Connected
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Integration Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      Voice Calls
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      SMS
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Video className="w-4 h-4 mr-2" />
                      Video
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Bell className="w-4 h-4 mr-2" />
                      Notifications
                    </span>
                    <Switch />
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="mb-4 text-2xl font-semibold">How It Works</h2>
          <Tabs defaultValue="connect" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="connect">Connect</TabsTrigger>
              <TabsTrigger value="configure">Configure</TabsTrigger>
              <TabsTrigger value="implement">Implement</TabsTrigger>
            </TabsList>
            <TabsContent value="connect" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">1. Connect Your Twilio Account</h3>
              <p>Enter your Twilio Account SID and Auth Token to establish a secure connection between platforms.</p>
            </TabsContent>
            <TabsContent value="configure" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">2. Configure Services</h3>
              <p>
                Set up phone numbers, messaging services, and other Twilio products you want to use in your application.
              </p>
            </TabsContent>
            <TabsContent value="implement" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">3. Implement Communication Features</h3>
              <p>
                Use Twilio's APIs to add voice, messaging, and other communication capabilities to your application.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <aside className="hidden w-64 p-6 bg-white border-l border-gray-200 lg:block">
        <h2 className="mb-4 text-lg font-semibold">Quick Links</h2>
        <nav className="space-y-2">
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Twilio Console
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            API Documentation
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Twilio Helper Libraries
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
        </nav>
        <div className="mt-8">
          <Badge variant="outline" className="mb-2">
            Pro Tip
          </Badge>
          <p className="text-sm text-gray-600">
            Use Twilio's TwiML for easy creation of voice and messaging applications.
          </p>
        </div>
      </aside>
    </div>
  );
}
